/**
 * Login Service
 * Handles user authentication, MFA verification, and token management
 */

import type { ApiErrorResponse } from '@/types/api';
import { extractErrorMessage } from '@/types/api';
import type {
  CompanyItem,
  LoginRequest,
  LoginResponse,
  MfaEnrollRequest,
  MfaEnrollResponse,
  MfaPolicyResponse,
  MfaPolicyUpdateResponse,
  MfaPolicyQueryParams,
  MfaPolicyUpdateRequest,
  MfaResetResponse,
  MfaSetupRequest,
  MfaSetupResponse,
  MfaStatusResponse,
  MfaVerifyRequest,
  PendingAuthResponse,
  SsoStartRequest,
  SsoStartResponse,
  SwitchCompanyRequest,
  User,
} from './types';

export class LoginService {
  /**
   * Authenticate user and set HTTP-only cookies.
   * If MFA is required, returns a pending-auth response instead.
   */
  static async login(credentials: LoginRequest): Promise<LoginResponse | PendingAuthResponse> {
    const response = await fetch('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }

  /**
   * Start an OIDC SSO authorization flow. This returns the provider URL only;
   * Moonitor cookies are set later by the callback route after validation.
   */
  static async startSso(payload: SsoStartRequest): Promise<SsoStartResponse> {
    const response = await fetch('/api/login/sso/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }

  /**
   * Verify an MFA challenge and complete login or company switching.
   */
  static async verifyMfa(payload: MfaVerifyRequest): Promise<LoginResponse> {
    const response = await fetch('/api/login/mfa/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }

  static async getMfaSetup(payload: MfaSetupRequest): Promise<MfaSetupResponse> {
    const response = await fetch('/api/login/mfa/setup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }

  static async enrollMfa(payload: MfaEnrollRequest): Promise<MfaEnrollResponse> {
    const response = await fetch('/api/login/mfa/enroll', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }

  /**
   * Get current user profile (requires valid access token in cookie)
   */
  static async getCurrentUser(): Promise<User> {
    const response = await fetch('/api/login', {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message.user;
  }

  /**
   * Refresh access token using refresh token from cookie
   */
  static async refreshToken(): Promise<void> {
    const response = await fetch('/api/login', {
      method: 'PATCH',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }
  }

  /**
   * Clear authentication cookies and end the current session.
   */
  static async logout(): Promise<void> {
    const response = await fetch('/api/logout', {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }
  }

  /**
   * Get the list of companies the authenticated user belongs to.
   */
  static async getCompanies(): Promise<CompanyItem[]> {
    const response = await fetch('/api/login/companies', {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message.companies;
  }

  /**
   * Switch to a different company. New auth cookies are set by the API route.
   * Returns the new user profile for the target company.
   */
  static async switchCompany(payload: SwitchCompanyRequest): Promise<User | PendingAuthResponse> {
    const response = await fetch('/api/login/switch-company', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    if (data.message?.pendingAuth === true) {
      return data.message;
    }
    return data.message.user;
  }

  static async getMfaStatus(): Promise<MfaStatusResponse> {
    const response = await fetch('/api/login/mfa/status', {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }

  static async getMfaPolicy(params?: MfaPolicyQueryParams): Promise<MfaPolicyResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
    if (params?.q) searchParams.set('q', params.q);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params?.enrollmentStatus) searchParams.set('enrollmentStatus', params.enrollmentStatus);
    const queryString = searchParams.toString();
    const response = await fetch(`/api/login/mfa/policy${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }

  static async updateMfaPolicy(payload: MfaPolicyUpdateRequest): Promise<MfaPolicyUpdateResponse> {
    const response = await fetch('/api/login/mfa/policy', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }

  static async resetUserMfa(userId: string): Promise<MfaResetResponse> {
    const response = await fetch(`/api/login/mfa/users/${encodeURIComponent(userId)}/reset`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new LoginError(error, response.status);
    }

    const data = await response.json();
    return data.message;
  }
}

/**
 * Custom error class for login operations
 */
export class LoginError extends Error {
  constructor(
    public readonly response: ApiErrorResponse,
    public readonly statusCode: number
  ) {
    super(extractErrorMessage(response, 'Login operation failed'));
    this.name = 'LoginError';
  }

  getErrorCode(): string | undefined {
    return this.response.error_code;
  }

  getErrorRef(): string | undefined {
    return (
      this.response.details?.ref ||
      (typeof this.response.detail === 'object' ? this.response.detail?.ref : undefined)
    );
  }

  getErrorData(): Record<string, unknown> | undefined {
    return (
      this.response.details?.data ||
      (typeof this.response.detail === 'object' ? this.response.detail?.data : undefined)
    );
  }
}
